import { useFBO, useScroll } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import * as React from "react";
import * as THREE from "three";
import type { SceneKey } from "./ScrollSceneController";
import { Scene, SectionRefs } from "./Scene";
import {
  DECKS_BG_COLOR,
} from "./constants";
import { clamp01 } from "./utils";
import type { SceneThresholds } from "./responsive";
import { useSceneScalars } from "../../hooks/useSceneScalars";
import "./TransitionMaterial";

// Reused every frame to position the fullscreen quad in front of the camera —
// avoids allocating a Vector3 per frame inside useFrame.
const _forward = new THREE.Vector3();

function getSectionsAndProgression(
  t: number,
  th: SceneThresholds,
): { from: SceneKey; to: SceneKey; progression: number } {
  if (t <= th.homeServices) {
    const progression = clamp01(t / th.homeServices);
    return { from: "home", to: "services", progression };
  }
  if (t < th.servicesDecks) {
    return { from: "services", to: "services", progression: 1 };
  }
  if (t < th.decksDwell) {
    const progression = clamp01(
      (t - th.servicesDecks) / (th.decksDwell - th.servicesDecks),
    );
    return { from: "services", to: "decks", progression };
  }
  // Reviews has no canvas transition — its HTML section uses an opaque white
  // background and scrolls up over the decks scene like the footer does.
  return { from: "decks", to: "decks", progression: 1 };
}

function setSectionVisibility(refs: SectionRefs, section: SceneKey) {
  if (refs.home.current) refs.home.current.visible = section === "home";
  if (refs.services.current)
    refs.services.current.visible = section === "services";
  if (refs.decks.current) refs.decks.current.visible = section === "decks";
  if (refs.reviews.current)
    refs.reviews.current.visible = section === "reviews";
}

type Props = {
  activeScene: SceneKey;
};

export function ScrollTransitionScene({ activeScene }: Props) {
  const scroll = useScroll();
  const scalars = useSceneScalars();
  const { gl, scene, camera, size } = useThree();
  // samples: 4 — enable MSAA on the FBO render path so box edges (and any other
  // geometry rendered into the cross-fade targets) are antialiased before the
  // shader composite. Without this, EffectComposer's MSAA is bypassed because
  // the section is already aliased by the time it reaches the composer.
  const fbo1 = useFBO({ samples: 4 });
  const fbo2 = useFBO({ samples: 4 });

  const sceneContentRef = React.useRef<THREE.Group>(null);
  const quadRef = React.useRef<THREE.Mesh>(null);

  // Keep the FBO textures in their default LinearSRGB color space (plain RGBA8
  // storage). Do NOT tag them SRGBColorSpace: that allocates SRGB8_ALPHA8
  // storage + a multisampled sRGB renderbuffer, and WebKit's resolve of sRGB
  // MSAA renderbuffers skips the color conversion — every midtone gets an
  // extra gamma encode and the whole services section washes out to pale gray
  // on iPhone Safari (Chrome renders both variants identically, verified
  // pixel-wise 2026-07-20). With RGBA8 there is no hardware sRGB conversion
  // anywhere in the FBO path, so the composite is engine-independent.

  const sectionRefs: SectionRefs = React.useMemo(
    () => ({
      home: React.createRef<THREE.Group>(),
      services: React.createRef<THREE.Group>(),
      decks: React.createRef<THREE.Group>(),
      reviews: React.createRef<THREE.Group>(),
    }),
    [],
  );

  // Per-section background color used during FBO render passes. `null` means
  // "leave whatever is there" — used by sections that paint their sky via
  // drei <Sky> (home, services), where the dome already covers the frame.
  const SCENE_BG = React.useMemo<Record<SceneKey, THREE.Color | null>>(
    () => ({
      home: null,
      services: null,
      decks: new THREE.Color(DECKS_BG_COLOR),
      reviews: null,
    }),
    [],
  );

  // Initial value matches the section progression at scroll=0 (= 0, meaning
  // the "from" section = home is fully shown). Starting at 1 was the original
  // behaviour, but it produces a ~1.5 s damped fade-in from services→home on
  // every fresh mount — invisible behind the initial Loader, but very visible
  // when remounting after a page-level transition (no Loader covering it).
  const smoothProgression = React.useRef(0);

  // Tracks the active from→to pair so we can detect when `progression` changes
  // meaning at a segment boundary (see the snap in useFrame).
  const prevSegmentRef = React.useRef("");

  React.useLayoutEffect(() => {
    const quad = quadRef.current;
    if (!quad) return;
    quad.raycast = () => {};
  }, []);

  useFrame((_, delta) => {
    const t = Math.min(scroll.offset * scalars.tScale, scalars.tMax);
    const { from, to, progression } = getSectionsAndProgression(t, scalars.thresholds);

    // When the from→to pair changes, the meaning of `progression` flips: e.g.
    // at th.servicesDecks the segment goes from services→services
    // (progression 1 = services) to services→decks (progression 0 = services).
    // Damping across that boundary would carry the stale value (~1) into the new
    // segment, where it now reads as "decks" — compositing a blue flash before
    // settling back to services and then climbing to decks again. Snap to the
    // new target so the damped value never crosses the semantic boundary.
    const segment = `${from}->${to}`;
    if (segment !== prevSegmentRef.current) {
      prevSegmentRef.current = segment;
      smoothProgression.current = progression;
    }

    // Dampen progression for a slower, smoother transition
    const lerpSpeed = 3;
    smoothProgression.current += (progression - smoothProgression.current) * Math.min(1, lerpSpeed * delta);

    const mat = quadRef.current?.material as
      | (THREE.ShaderMaterial & {
          tex: THREE.Texture;
          tex2: THREE.Texture;
          progression: number;
        })
      | undefined;
    const quad = quadRef.current;
    const sceneContent = sceneContentRef.current;

    if (!mat || !sceneContent || !quad) {
      gl.setRenderTarget(null);
      if (quad) quad.visible = true;
      return;
    }

    sceneContent.visible = true;
    quad.visible = false;

    const prevBg = scene.background;

    setSectionVisibility(sectionRefs, from);
    scene.background = SCENE_BG[from] ?? prevBg;
    gl.setRenderTarget(fbo1);
    gl.clear();
    gl.render(scene, camera);

    setSectionVisibility(sectionRefs, to);
    scene.background = SCENE_BG[to] ?? prevBg;
    gl.setRenderTarget(fbo2);
    gl.clear();
    gl.render(scene, camera);

    scene.background = prevBg;

    sceneContent.visible = false;
    quad.visible = true;
    gl.setRenderTarget(null);

    mat.tex = fbo1.texture;
    mat.tex2 = fbo2.texture;
    mat.progression = smoothProgression.current;

    const cam = camera as THREE.PerspectiveCamera;
    const dist = 5;
    const vFov = (cam.fov * Math.PI) / 180;
    const h = 2 * Math.tan(vFov / 2) * dist;
    const w = h * (size.width / size.height);
    quad.position.copy(cam.position);
    quad.quaternion.copy(cam.quaternion);
    quad.position.addScaledVector(
      _forward.set(0, 0, -1).applyQuaternion(cam.quaternion),
      dist
    );
    quad.scale.set(w, h, 1);
  });

  return (
    <>
      <group ref={sceneContentRef}>
        <Scene activeScene={activeScene} sectionRefs={sectionRefs} />
      </group>
      <mesh ref={quadRef} frustumCulled={false}>
        <planeGeometry args={[2, 2]} />
        {/* @ts-expect-error transitionMaterial is extended via extend() */}
        <transitionMaterial
          tex={fbo1.texture}
          tex2={fbo2.texture}
          progression={1}
          transition={0}
          depthWrite={false}
        />
      </mesh>
    </>
  );
}
